import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { assessmentService } from '@/services/assessment.service';
import { PlusCircle, FileText, Edit, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const AssessmentsTable = ({ data, isLoading, type }) => {
  if (isLoading) {
    return <div className="text-center p-8">Loading...</div>;
  }

  if (!data || data.length === 0) {
    return <div className="text-center p-8">No {type} found.</div>;
  }

  return (
    <table className="w-full">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Submissions</th>
          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {data.map((item) => (
          <tr key={item.id} className="hover:bg-gray-50">
            <td className="px-4 py-4 font-medium text-gray-900">{item.title}</td>
            <td className="px-4 py-4 text-gray-600">{item.course}</td>
            <td className="px-4 py-4 text-center">{item.submissions}</td>
            <td className="px-4 py-4">
              <div className="flex items-center justify-end gap-2">
                <button className="p-1 text-gray-400 hover:text-gray-600"><Edit className="w-4 h-4" /></button>
                <button className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default function Assessments() {
  const [activeTab, setActiveTab] = useState('quizzes');

  const { data: quizzes, isLoading: isLoadingQuizzes } = useQuery({
    queryKey: ['quizzes'],
    queryFn: () => assessmentService.getQuizzes(),
    enabled: activeTab === 'quizzes',
  });

  const { data: assignments, isLoading: isLoadingAssignments } = useQuery({
    queryKey: ['assignments'],
    queryFn: () => assessmentService.getAssignments(),
    enabled: activeTab === 'assignments',
  });

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold">Assessments</h1>
        <p className="text-green-100">Manage quizzes, exams, and assignments for your courses.</p>
      </div>

      <div className="flex justify-between items-center">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button onClick={() => setActiveTab('quizzes')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'quizzes' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              Quizzes & Exams
            </button>
            <button onClick={() => setActiveTab('assignments')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'assignments' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              Assignments
            </button>
          </nav>
        </div>
        <button className="btn btn-primary inline-flex items-center gap-2">
          <PlusCircle size={20} />
          <span>Create New</span>
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {activeTab === 'quizzes' && <AssessmentsTable data={quizzes?.data} isLoading={isLoadingQuizzes} type="quizzes" />}
          {activeTab === 'assignments' && <AssessmentsTable data={assignments?.data} isLoading={isLoadingAssignments} type="assignments" />}
        </div>
      </div>
    </div>
  );
}
